import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import AudioModal from './AudioModal';
import TagsModal from './TagsModal';
import PhotoDetailModal from './PhotoDetailModal';
import { updatePhotoInAllCaches } from '../utils/photoCache';
import { imageCache } from '../utils/imageCache';

interface Photo {
  photoId: string;
  imageUrl: string;
  thumbnailUrls?: {
    small: string;
    medium: string;
    large: string;
  };
  ownerId: string;
  ownerName: string;
  likeCount: number;
  userHasLiked?: boolean;
  commentCount?: number;
  aspectRatio?: number;
  createdAt: string;
}

interface PhotoTileProps {
  photo: Photo;
  onDeleted: () => void;
  initiallyOpen?: boolean;
}

export default function PhotoTile({ photo, onDeleted, initiallyOpen = false }: PhotoTileProps) {
  const { user, isAdmin } = useAuthStore();
  const queryClient = useQueryClient();
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLikesTooltip, setShowLikesTooltip] = useState(false);
  const [showAudioTooltip, setShowAudioTooltip] = useState(false);
  const [showCommentsTooltip, setShowCommentsTooltip] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isLiked, setIsLiked] = useState(photo.userHasLiked || false);
  const [likeCount, setLikeCount] = useState(photo.likeCount);
  const [commentCount, setCommentCount] = useState(photo.commentCount ?? 0);
  const [imageSrc, setImageSrc] = useState<string | undefined>(
    photo.thumbnailUrls?.medium || photo.imageUrl
  );
  const [fullImageSrc, setFullImageSrc] = useState<string | undefined>(photo.imageUrl);
  const [hasRetriedImage, setHasRetriedImage] = useState(false);
  const [isImageRefreshing, setIsImageRefreshing] = useState(false);

  const isOwner = user?.userId === photo.ownerId;
  const canDelete = isOwner || isAdmin;

  // Open modal if initiallyOpen prop is true
  useEffect(() => {
    if (initiallyOpen) {
      setShowDetailModal(true);
    }
  }, [initiallyOpen, photo.photoId]);

  useEffect(() => {
    setIsLiked(photo.userHasLiked || false);
  }, [photo.userHasLiked]);

  useEffect(() => {
    setLikeCount(photo.likeCount);
  }, [photo.likeCount]);

  useEffect(() => {
    setCommentCount(photo.commentCount ?? 0);
  }, [photo.commentCount]);

  useEffect(() => {
    let isMounted = true;

    async function loadImage() {
      // Try to load from cache first
      const cachedUrl = await imageCache.get(photo.photoId, 'medium');
      
      if (isMounted) {
        if (cachedUrl) {
          // Use cached image
          setImageSrc(cachedUrl);
        } else {
          // Use presigned URL and cache it in background
          const presignedUrl = photo.thumbnailUrls?.medium || photo.imageUrl;
          setImageSrc(presignedUrl);
          
          // Cache in background (don't await)
          if (presignedUrl) {
            imageCache.set(photo.photoId, 'medium', presignedUrl).catch((err) => {
              console.error('Background caching failed:', err);
            });
          }
        }
        
        // Always set full image for detail modal
        setFullImageSrc(photo.imageUrl);
        setHasRetriedImage(false);
      }
    }

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [photo.thumbnailUrls?.medium, photo.imageUrl, photo.photoId]);

  // Fetch likes when tooltip is shown
  const { data: likesData, isLoading: isLoadingLikes, error: likesError } = useQuery({
    queryKey: ['photo-likes', photo.photoId],
    queryFn: () => api.listLikes(photo.photoId),
    enabled: showLikesTooltip && likeCount > 0,
  });

  // Fetch messages when tooltip is shown
  const { data: messagesData } = useQuery({
    queryKey: ['messages', photo.photoId],
    queryFn: () => api.listMessages(photo.photoId),
    enabled: showAudioTooltip,
  });

  // Fetch comments when tooltip is shown
  const { data: commentsData, isLoading: isLoadingComments, error: commentsError } = useQuery({
    queryKey: ['comments', photo.photoId],
    queryFn: () => api.listComments(photo.photoId),
    enabled: showCommentsTooltip && commentCount > 0,
  });

  // Get unique authors for audio messages (one per person)
  interface AudioMessageAuthor {
    authorId: string;
    authorName: string;
  }
  
  const messages = messagesData?.messages || [];
  const audioMessageAuthors = messages.reduce((acc: AudioMessageAuthor[], message: { authorId: string; authorName: string }) => {
    if (!acc.find((m: AudioMessageAuthor) => m.authorId === message.authorId)) {
      acc.push({
        authorId: message.authorId,
        authorName: message.authorName,
      });
    }
    return acc;
  }, [] as AudioMessageAuthor[]);
  
  const audioMessageCount = audioMessageAuthors.length;

  const likeMutation = useMutation({
    mutationFn: () =>
      isLiked ? api.unlikePhoto(photo.photoId) : api.likePhoto(photo.photoId),
    onSuccess: () => {
      const newIsLiked = !isLiked;
      const newLikeCount = Math.max(0, newIsLiked ? likeCount + 1 : likeCount - 1);
      setIsLiked(newIsLiked);
      setLikeCount(newLikeCount);
      queryClient.invalidateQueries({ queryKey: ['photo-likes', photo.photoId] });
      updatePhotoInAllCaches(queryClient, photo.photoId, (existing) => ({
        ...existing,
        userHasLiked: newIsLiked,
        likeCount: newLikeCount,
      }));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deletePhoto(photo.photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['photos'] });
      onDeleted();
    },
  });

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    deleteMutation.mutate();
    setShowDeleteConfirm(false);
  };

  const handleImageClick = () => {
    setShowDetailModal(true);
  };

  const handleLikeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    likeMutation.mutate();
  };

  const handleAudioClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowAudioModal(true);
  };

  const handleTagsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowTagsModal(true);
  };

  return (
    <>
      <div className="photo-tile relative mb-1 group cursor-pointer">
        {/* Image Container */}
        <div
          className="relative rounded-sm overflow-hidden bg-gray-100 shadow-[0_3px_12px_rgba(0,0,0,0.15)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.20)] transition-all duration-300 cursor-pointer"
          onClick={handleImageClick}
        >
          {imageSrc ? (
            <div className="relative w-full" style={{ aspectRatio: photo.aspectRatio || '1' }}>
              <img
                src={imageSrc}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                onLoad={(e) => {
                  (e.target as HTMLImageElement).classList.add('loaded');
                }}
                onError={async (e) => {
                  const target = e.target as HTMLImageElement;

                  // First retry: fetch a fresh pre-signed URL from the API
                  if (!hasRetriedImage && !isImageRefreshing) {
                    setHasRetriedImage(true);
                    setIsImageRefreshing(true);
                    try {
                      const freshPhoto = await api.getPhoto(photo.photoId);
                      const nextSrc = freshPhoto.thumbnailUrls?.medium || freshPhoto.imageUrl || '';
                      setImageSrc(nextSrc);
                      setFullImageSrc(freshPhoto.imageUrl);
                      updatePhotoInAllCaches(queryClient, photo.photoId, (existing) => ({
                        ...existing,
                        imageUrl: freshPhoto.imageUrl,
                        thumbnailUrls: freshPhoto.thumbnailUrls,
                      }));
                      target.src = nextSrc;
                      return;
                    } catch (refreshError) {
                      console.error('Failed to refresh photo URL', refreshError);
                    } finally {
                      setIsImageRefreshing(false);
                    }
                  }

                  // Final fallback: hide broken image and show placeholder message
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent && !parent.querySelector('.image-error')) {
                    const errorDiv = document.createElement('div');
                    errorDiv.className = 'image-error w-full h-full bg-gray-200 flex items-center justify-center text-gray-400 text-sm';
                    errorDiv.textContent = 'Image not available';
                    parent.appendChild(errorDiv);
                  }
                }}
              />
            </div>
          ) : (
            <div className="w-full h-64 bg-gray-200 flex items-center justify-center text-gray-400 text-sm">
              <div className="text-center">
                <p className="text-xs">No image URL</p>
                <p className="text-[10px] mt-1 text-gray-300">Photo ID: {photo.photoId}</p>
              </div>
            </div>
          )}

          {/* Actions overlay - show on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-3 flex flex-wrap items-center justify-between gap-1.5 sm:gap-2 pointer-events-auto">
              <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                {/* Like button with tooltip */}
                <div 
                  className="relative"
                  onMouseEnter={() => likeCount > 0 && setShowLikesTooltip(true)}
                  onMouseLeave={() => setShowLikesTooltip(false)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={handleLikeClick}
                    disabled={likeMutation.isPending}
                    className="flex items-center gap-1 text-white hover:scale-110 transition-transform bg-black/70 backdrop-blur-md rounded-full p-1.5 sm:p-2 border border-white/30 shadow-lg"
                  >
                    <svg
                      className={`w-4 h-4 sm:w-5 sm:h-5 transition-all ${isLiked ? 'fill-red-500 text-red-500 scale-110' : ''}`}
                      fill={isLiked ? 'currentColor' : 'none'}
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                      />
                    </svg>
                    {likeCount > 0 && (
                      <span className="text-xs sm:text-sm font-semibold pr-0.5">{likeCount}</span>
                    )}
                  </button>

                  {/* Likes tooltip */}
                  {showLikesTooltip && likeCount > 0 && (
                    <div 
                      className="absolute bottom-full left-0 mb-2 w-56 sm:w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50 animate-slide-down"
                      onMouseEnter={() => setShowLikesTooltip(true)}
                      onMouseLeave={() => setShowLikesTooltip(false)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="text-xs font-semibold text-gray-700 mb-2">
                        Liked by {likeCount} {likeCount === 1 ? 'person' : 'people'}
                      </div>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto scrollbar-hide">
                        {likesError ? (
                          <div className="text-xs text-red-400">Error loading likes</div>
                        ) : isLoadingLikes ? (
                          <div className="text-xs text-gray-400">Loading...</div>
                        ) : likesData?.likes && Array.isArray(likesData.likes) && likesData.likes.length > 0 ? (
                          likesData.likes.map((like: { userId: string; name: string; email: string }) => (
                            <div
                              key={like.userId}
                              className="text-xs text-gray-600 truncate"
                              title={like.email}
                            >
                              {like.name}
                            </div>
                          ))
                        ) : (
                          <div className="text-xs text-gray-400">No likes yet</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Comments summary with tooltip */}
                <div 
                  className="relative"
                  onMouseEnter={() => commentCount > 0 && setShowCommentsTooltip(true)}
                  onMouseLeave={() => setShowCommentsTooltip(false)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDetailModal(true);
                    }}
                    className="flex items-center gap-1 text-white bg-black/70 backdrop-blur-md rounded-full p-1.5 sm:p-2 border border-white/30 shadow-lg hover:scale-110 transition-transform"
                    title="View comments"
                  >
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                    {commentCount > 0 && (
                      <span className="text-xs sm:text-sm font-semibold pr-0.5">{commentCount}</span>
                    )}
                  </button>

                  {/* Comments tooltip */}
                  {showCommentsTooltip && commentCount > 0 && (
                    <div 
                      className="absolute bottom-full left-0 mb-2 w-64 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50 animate-slide-down"
                      onMouseEnter={() => setShowCommentsTooltip(true)}
                      onMouseLeave={() => setShowCommentsTooltip(false)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="text-xs font-semibold text-gray-700 mb-2">
                        {commentCount} {commentCount === 1 ? 'comment' : 'comments'}
                      </div>
                      <div className="space-y-1.5 max-h-32 overflow-y-auto scrollbar-hide">
                        {commentsError ? (
                          <div className="text-xs text-red-400">Error loading comments</div>
                        ) : isLoadingComments ? (
                          <div className="text-xs text-gray-400">Loading...</div>
                        ) : commentsData?.comments && Array.isArray(commentsData.comments) && commentsData.comments.length > 0 ? (
                          <>
                            {commentsData.comments.slice(0, 3).map((comment: { commentId: string; authorName: string; authorEmail: string; content: string }) => (
                              <div
                                key={comment.commentId}
                                className="text-xs text-gray-600"
                                title={comment.authorEmail}
                              >
                                <div className="font-semibold truncate">{comment.authorName}</div>
                                <div className="text-gray-500 line-clamp-2 italic">&quot;{comment.content}&quot;</div>
                              </div>
                            ))}
                            {commentsData.comments.length > 3 && (
                              <div className="text-xs text-primary-600 font-medium pt-1 border-t border-gray-100">
                                +{commentsData.comments.length - 3} more · Click to view all
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-xs text-gray-400">No comments yet</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                {/* Audio button with count and tooltip */}
                <div 
                  className="relative"
                  onMouseEnter={() => audioMessageCount > 0 && setShowAudioTooltip(true)}
                  onMouseLeave={() => setShowAudioTooltip(false)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={handleAudioClick}
                    className="flex items-center gap-1 text-white hover:scale-110 transition-transform bg-black/70 backdrop-blur-md rounded-full p-1.5 sm:p-2 border border-white/30 shadow-lg"
                    title="Audio messages"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                    {audioMessageCount > 0 && (
                      <span className="text-xs sm:text-sm font-semibold pr-0.5">{audioMessageCount}</span>
                    )}
                  </button>

                  {/* Audio Tooltip */}
                  {showAudioTooltip && audioMessageCount > 0 && (
                    <div 
                      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 sm:w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50 animate-slide-down"
                      onMouseEnter={() => setShowAudioTooltip(true)}
                      onMouseLeave={() => setShowAudioTooltip(false)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="text-xs font-semibold text-gray-700 mb-2">
                        Audio messages from {audioMessageCount} {audioMessageCount === 1 ? 'person' : 'people'}
                      </div>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto scrollbar-hide">
                        {audioMessageAuthors.map((author: AudioMessageAuthor) => (
                          <div
                            key={author.authorId}
                            className="text-xs text-gray-600 truncate"
                          >
                            {author.authorName}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={handleTagsClick}
                  className="p-1.5 sm:p-2 bg-black/70 backdrop-blur-md rounded-full hover:bg-black/80 transition-colors border border-white/30 shadow-lg"
                  title="Tags"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </button>

                {canDelete && (
                  <button
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                    className="p-1.5 sm:p-2 bg-red-600 backdrop-blur-md rounded-full hover:bg-red-700 transition-colors border border-red-300 shadow-lg disabled:opacity-60"
                    title="Delete photo"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showDetailModal && (
        <PhotoDetailModal
          photo={{
            ...photo,
            imageUrl: fullImageSrc || photo.imageUrl,
            thumbnailUrls: photo.thumbnailUrls,
          }}
          onClose={() => setShowDetailModal(false)}
        />
      )}

      {showAudioModal && (
        <AudioModal
          photoId={photo.photoId}
          onClose={() => setShowAudioModal(false)}
        />
      )}

      {showTagsModal && (
        <TagsModal
          photoId={photo.photoId}
          onClose={() => setShowTagsModal(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-in">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
              Delete Photo
            </h3>
            <p className="text-sm sm:text-base text-gray-600 mb-6">
              Are you sure you want to delete this photo? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm sm:text-base text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm sm:text-base bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
